import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  Stack,
  IconButton,
  LinearProgress,
  Input,
  FormLabel,
} from '@mui/joy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DocxTemplateUpload = ({
  currentTemplate,
  templateUrl,
  onTemplateUpload,
  onTemplateRemove,
  photoWidthMm = 120,
  onPhotoWidthChange,
  uploading = false
}) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      onTemplateUpload(file);
    } else {
      alert('Please select a valid DOCX file.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Stack spacing={2}>
      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
        Upload a DOCX template file. Use {'{{variable}}'} placeholders for dynamic content. Use {'{{Photos[0]}}'}, {'{{Photos[1]}}'} etc. to embed photos, or loop with {'{% for p in Photos %}{{p}}{% endfor %}'}.
      </Typography>

      {currentTemplate || templateUrl ? (
        // Show current template
        <Card
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.level1'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#2563eb20',
                borderRadius: 'sm',
                color: '#2563eb'
              }}
            >
              <InsertDriveFileIcon />
            </Box>
            <Box>
              <Typography level="title-sm" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentTemplate?.name || 'template.docx'}
                <CheckCircleIcon sx={{ fontSize: '16px', color: 'success.main' }} />
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {currentTemplate?.size ? formatFileSize(currentTemplate.size) : 'Template uploaded'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip color="success" size="sm" variant="soft">
              Ready
            </Chip>
            <IconButton
              size="sm"
              color="danger"
              variant="soft"
              onClick={onTemplateRemove}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Card>
      ) : (
        // Show upload area
        <Card
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: dragOver ? '#2563eb' : 'neutral.outlinedBorder',
            bgcolor: dragOver ? '#2563eb08' : 'background.level1',
            '&:hover': {
              borderColor: '#2563eb',
              bgcolor: '#2563eb08'
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: dragOver ? '#2563eb' : 'text.secondary',
              mb: 1
            }}
          />
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Drop DOCX template here or click to browse
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
            Supports .docx files up to 10MB
          </Typography>
        </Card>
      )}

      {uploading && (
        <Box>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            Uploading template...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      <Box>
        <FormLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>Photo width (mm)</FormLabel>
        <Input
          type="number"
          size="sm"
          value={photoWidthMm}
          slotProps={{ input: { min: 10, max: 400 } }}
          onChange={(e) => onPhotoWidthChange && onPhotoWidthChange(Number(e.target.value))}
          endDecorator={<Typography level="body-xs">mm</Typography>}
          sx={{ width: 120 }}
        />
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files[0])}
      />
    </Stack>
  );
};

export default DocxTemplateUpload;